import { parseDate } from './index';
import { expRegURL } from './expReg';
import { isAfter, isBefore, isDate, isEqual, isValid, isSameDay } from 'date-fns';

/**
 * This function validate field according to the established rule
 @param {Object} name - name of field
 @param {Object} value - value of field
 @param {Object} rule - rule of field
 */

const validateInput = (name, value, rule) => {
  const output = { passes: true, message: null };
  if (rule === 'required' && (value === null || value === undefined || value === '')) {
    output.passes = false;
    output.message = 'The field is required.';
  } else if (rule === 'validObjectId' && value && !/^[0-9a-fA-F]{24}$/.test(value)) {
    output.passes = false;
    output.message = 'The field must be a valid ObjectId.';
  } else if (rule === 'required' && value.length === 0) {
    output.passes = false;
    output.message = 'The field is required.';
  } else if (rule === 'number' && isNaN(value)) {
    output.passes = false;
    output.message = 'The field must be a number.';
  } else if (rule === 'numberNegative' && parseInt(value) > 0) {
    output.passes = false;
    output.message = 'The field must be a negative number.';
  } else if (rule === 'password' && !/^(?=.*[^A-Za-z0-9]).{8,}$/g.test(value)) {
    output.passes = false;
    output.message =
      'The field must be at least 8 characters long and contain at least one special character.';
  } else if (rule === 'numberPositive' && parseInt(value) < 0) {
    output.passes = false;
    output.message = 'The field must be a positive number.';
  } else if (/match:(.*)/.test(rule)) {
    const data = rule.match(/match:(.*)/);
    const match = data[1];
    if (value !== match) {
      output.passes = false;
      output.message = 'The field must match the password.';
    }
  } else if (/length:([0-9]+)/.test(rule)) {
    const data = rule.match(/length:([0-9]+)/);
    const length = data[1];
    if (parseInt(value.length) !== parseInt(length)) {
      output.passes = false;
      output.message = `The field must be ${length} digits.`;
    }
  } else if (rule === 'withoutSpaces' && /\s/.test(value)) {
    output.passes = false;
    output.message = 'The field must not contain spaces.';
  } else if (rule === 'date') {
    if (value) {
      const currentDate = new Date(value);
      const isDateRule = value ? isDate(currentDate) : '';
      const isValidRule = value ? isValid(currentDate) : '';
      if (!isValidRule || !isDateRule) {
        output.passes = false;
        output.message = 'Invalid date.';
      }
    }
  } else if (value && rule === 'url' && !expRegURL.test(value)) {
    output.passes = false;
    output.message = 'The field must be an URL.';
  } else if (value && rule === 'email' && !/\S+@\S+/.test(value)) {
    output.passes = false;
    output.message = 'The field must be an email address.';
  } else if (/between:([0-9]+),([0-9]+)/.test(rule)) {
    const data = rule.match(/between:([0-9]+),([0-9]+)/);
    const min = data[1];
    const max = data[2];

    if (value < min || value > max) {
      output.passes = false;
      output.message = `The field must be between ${min} - ${max}.`;
    }
  } else if (/min:([0-9]+)/.test(rule)) {
    const data = rule.match(/min:([0-9]+)/);
    const min = data[1];

    if (value < min) {
      output.passes = false;
      output.message = `The field must greater than to ${min}.`;
    }
  } else if (/max:([0-9]+)/.test(rule)) {
    const data = rule.match(/max:([0-9]+)/);
    const max = data[1];

    if (value > max) {
      output.passes = false;
      output.message = `The field must be less than to ${max}.`;
    }
  } else if (/betweenDate:(.*),(.*)/.test(rule)) {
    const data = rule.match(/betweenDate:(.*),(.*)/);
    const date = new Date(value);
    const max = new Date(data[1]);
    const min = new Date(data[1]);

    // FIXME: revisar validacion
    if (isBefore(min, date) || isAfter(max, date)) {
      output.passes = false;
      output.message = `The field must be between ${parseDate(min)} - ${parseDate(max)}.`;
    }
  } else if (/minDate:(.*)/.test(rule)) {
    const data = rule.match(/minDate:(.*)/);
    const date = new Date(value);
    const min = new Date(data[1]);
    if (isAfter(new Date(min), new Date(date))) {
      if (isSameDay(new Date(min), new Date(date)) === false) {
        output.passes = false;
        output.message = `The field must greater than to ${parseDate(min)}.`;
      }
    }
  } else if (/maxDate:(.*)/.test(rule)) {
    const data = rule.match(/maxDate:(.*)/);
    const date = new Date(value);
    const max = new Date(data[1]);

    if (isBefore(new Date(max), new Date(date))) {
      if (isSameDay(new Date(max), new Date(date)) === false) {
        output.passes = false;
        output.message = `The field must be less or equal to ${parseDate(max)}.`;
      }
    }
  }

  return output;
};

/**
 * This function validate form according to the established rules by each field
 @param {Object} fieldRules - Object of rules field
 @param {Object} form - Object of values form
 */

const validate = (fieldRules, form) => {
  const errors = {};
  Object.keys(fieldRules).forEach((field) => {
    const rules = fieldRules[field].split('|');
    let shouldStopValidating = false;

    rules.forEach((rule) => {
      if (!shouldStopValidating) {
        const validation = validateInput(field, form[field], rule);
        if (!validation.passes) {
          errors[field] = validation.message;
          shouldStopValidating = true;
        }
      }
    });
  });

  return errors;
};
/**
 * This function validate if string is url
 @param {String} url
 */
const isValidURL = (url) => expRegURL.test(url);

/**
 * This function validate if lc is duplicate
 @param {Array} list
 @param {Object} data
 */
const verifyDuplicateLC = (list = [], data) => {
  return list.some(
    (item) =>
      String(item._id) !== String(data._id) &&
      String(item.learningArea).toLowerCase() === String(data.learningArea).toLowerCase() &&
      String(item.discipline).toLowerCase() === String(data.discipline).toLowerCase() &&
      String(item.mode).toLowerCase() === String(data.mode).toLowerCase() &&
      String(item.strand).toLowerCase() === String(data.strand).toLowerCase() &&
      String(item.subStrand).toLowerCase() === String(data.subStrand).toLowerCase() &&
      String(item.organisingElements).toLowerCase() === String(data.organisingElements).toLowerCase() &&
      String(item.skills).toLowerCase() === String(data.skills).toLowerCase()
  );
};

export { validate, validateInput, isValidURL, verifyDuplicateLC };
